import { createSelector } from '@ngrx/store';
import { WritableDraft } from 'immer/dist/internal';
import { createSlice, PayloadAction } from 'ngrx-slice';

import {
  OfferApprovalRequest,
  OfferApprovalResponse,
  OfferCalculationStatusResponse,
  OfferDetailedResponse,
  OfferRequestOffersResponse,
  OfferResponse,
  OfferSiteResponse,
  PriceWrapper,
} from '@app/api/marketplace/models';
import { PaymentMethodType } from '@shared/enums/payment-method-type';
import { paramConvert } from '@shared/helpers/service-helpers';

import { OfferCalculationStatus } from '../enums/offer-calculation-status.enum';
import { OfferResponseOptionsCount } from '../interfaces/offer-response-stats.interface';

export interface OffersSortFilter {
  deliveryMethod?: string;
  paymentMethod?: string;
  deliveryDate?: number;
  maxNumberOfSuppliers?: number;
  hideUnreachedLimits?: boolean;
}

export interface State {
  offerApprovalRequest: OfferApprovalRequest;
  offerCalculationStatus: OfferCalculationStatus | null;
  offerRequestId: string | null;
  creationOffers: OfferRequestOffersResponse | null;
  offerDetailedResponse: OfferDetailedResponse | null;
  offerDetailLoading: boolean;
  offerLoading: boolean;
  optionsCount: OfferResponseOptionsCount;
  priceOffers: Array<OfferResponse>;
  deliveryOffers: Array<OfferResponse>;
  supplierOffers: Array<OfferResponse>;
  leastDiffOffers: Array<OfferResponse>;
}

export const initialState: State = {
  offerApprovalRequest: {
    offerSiteIdPaymentMethod: {},
  },
  offerCalculationStatus: null,
  offerRequestId: null,
  creationOffers: null,
  offerDetailedResponse: null,
  offerLoading: false,
  offerDetailLoading: false,
  optionsCount: {
    personalPickup: 0,
    delivery: 0,
    combined: 0,
    deliveryAll: 0,
    cash: 0,
    card: 0,
    bankTransfer: 0,
    paymentAll: 0,
  },
  priceOffers: [],
  deliveryOffers: [],
  supplierOffers: [],
  leastDiffOffers: [],
};

function filterOffers(
  filterName: 'paymentMethod' | 'deliveryMethod' | '',
  clearedFilter: any,
  offers: Array<OfferResponse>
): Array<OfferResponse> {
  let offersForSort = [...(offers ?? [])];
  if (
    offersForSort.some(
      (offer) => (offer.labels![0] ?? '') !== 'LEAST_DIFF_FROM_MINIMUM_ORDER'
    )
  ) {
    offersForSort = offersForSort.filter((offer) => {
      const normalOffer =
        (offer.labels![0] ?? '') !== 'LEAST_DIFF_FROM_MINIMUM_ORDER';
      return normalOffer;
    });
  }
  offersForSort = offersForSort.filter((offer) => {
    return Object.keys(clearedFilter).every((key) => {
      switch (key) {
        case 'paymentMethod': {
          return filterName === 'paymentMethod'
            ? true
            : offer.paymentMethods?.includes(clearedFilter['paymentMethod']);
        }
        case 'deliveryMethod': {
          return filterName === 'deliveryMethod'
            ? true
            : offer.labels?.some((label) =>
                label.includes(clearedFilter['deliveryMethod'])
              );
        }
        case 'deliveryDate': {
          return (
            Math.ceil(
              (new Date(offer.deliveryEndDate ?? '').getTime() -
                new Date().getTime()) /
                (1000 * 3600 * 24)
            ) <= clearedFilter['deliveryDate']
          );
        }
        case 'maxNumberOfSuppliers': {
          return (
            (offer.supplierCount ?? 0) <= clearedFilter['maxNumberOfSuppliers']
          );
        }
        case 'hideUnreachedLimits': {
          return !offer.labels?.includes('LEAST_DIFF_FROM_MINIMUM_ORDER');
        }
        default:
          return true;
      }
    });
  });
  return offersForSort;
}

export const {
  actions: OffersActions,
  selectors,
  ...OffersFeature
} = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    createOffer: {
      trigger: (state) => {
        state.offerCalculationStatus = OfferCalculationStatus.IN_PROGRESS;
        state.offerRequestId = null;
      },
      success: () => {},
      failure: (state) => ({
        ...state,
        offerCalculationStatus: null,
      }),
    },
    getOfferCreationStatus: {
      trigger: (
        state,
        _action: PayloadAction<{
          id: string;
          triggerer: 'recalculate' | 'creation';
        }>
      ) => {
        state.offerRequestId = null;
      },
      success: (
        state,
        action: PayloadAction<{ response: OfferCalculationStatusResponse }>
      ) => {
        state.offerCalculationStatus = action.response
          .status as OfferCalculationStatus;
        state.offerRequestId = action.response.offerRequestId ?? null;
      },
      failure: (state) => ({
        ...state,
        offerCalculationStatus: null,
      }),
    },
    loadCreationOffers: {
      trigger: (state, action: PayloadAction<{ offerRequestId: string }>) => {
        state.creationOffers = null;
        state.offerLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{ response: OfferRequestOffersResponse }>
      ) => {
        state.creationOffers = action.response;
        state.offerLoading = false;
      },
      failure: (state) => ({
        ...state,
        offerLoading: false,
      }),
    },
    sortOffers: {
      trigger: (
        state,
        action: PayloadAction<{ filter?: OffersSortFilter }>
      ) => {
        const clearedFilter = paramConvert(action.filter);
        const offersForPaymentSort = filterOffers(
          'paymentMethod',
          clearedFilter,
          [...(state.creationOffers?.offers ?? [])]
        );
        const offersForDeliverySort = filterOffers(
          'deliveryMethod',
          clearedFilter,
          [...(state.creationOffers?.offers ?? [])]
        );
        const offersForSort = filterOffers('', clearedFilter, [
          ...(state.creationOffers?.offers ?? []),
        ]);

        state.optionsCount = {
          personalPickup:
            offersForDeliverySort?.filter((offer) =>
              offer.labels?.some((label) => label.includes('PICKUP'))
            ).length ?? 0,
          delivery:
            offersForDeliverySort?.filter((offer) =>
              offer.labels?.some((label) => label.includes('DELIVERY'))
            ).length ?? 0,
          combined:
            offersForDeliverySort?.filter((offer) =>
              offer.labels?.some((label) => label.includes('COMBINED'))
            ).length ?? 0,
          deliveryAll: offersForDeliverySort.length,
          card:
            offersForPaymentSort?.filter((offer) =>
              offer.paymentMethods?.includes('CARD')
            ).length ?? 0,
          cash:
            offersForPaymentSort?.filter((offer) =>
              offer.paymentMethods?.includes('CASH')
            ).length ?? 0,
          bankTransfer:
            offersForPaymentSort?.filter((offer) =>
              offer.paymentMethods?.includes('BANK_TRANSFER')
            ).length ?? 0,
          paymentAll: offersForPaymentSort.length,
        };
        state.priceOffers = [
          ...(offersForSort
            .filter((offer) =>
              offer.labels?.some(
                (label) =>
                  label.includes('CHEAPEST') &&
                  (clearedFilter['deliveryMethod']
                    ? label.includes(clearedFilter['deliveryMethod'])
                    : true)
              )
            )
            .sort(
              (offer1, offer2) =>
                offer1?.sumPrice?.grossPrice! - offer2?.sumPrice?.grossPrice!
            ) ?? []),
        ];
        state.deliveryOffers = [
          ...(offersForSort
            .filter((offer) =>
              offer.labels?.some(
                (label) =>
                  label.includes('FASTEST') &&
                  (clearedFilter['deliveryMethod']
                    ? label.includes(clearedFilter['deliveryMethod'])
                    : true)
              )
            )
            .sort(
              (offer1, offer2) =>
                <any>new Date(offer1?.deliveryEndDate ?? '') -
                <any>new Date(offer2?.deliveryEndDate ?? '')
            ) ?? []),
        ];
        state.supplierOffers = [
          ...(offersForSort
            .filter((offer) =>
              offer.labels?.some(
                (label) =>
                  label.includes('LEAST_SUPPLIER') &&
                  (clearedFilter['deliveryMethod']
                    ? label.includes(clearedFilter['deliveryMethod'])
                    : true)
              )
            )
            .sort(
              (offer1, offer2) =>
                (offer1?.supplierCount ?? 0) - (offer2?.supplierCount ?? 0)
            ) ?? []),
        ];
        state.leastDiffOffers = [
          ...(offersForSort
            .filter((offer) =>
              offer.labels?.some((label) => label.includes('LEAST_DIFF'))
            )
            /* TODO ezt a sortot majd lehet másszerint kell implementálni*/
            .sort((offer1, offer2) => {
              return (
                offer1?.sumPrice?.grossPrice! - offer2?.sumPrice?.grossPrice!
              );
            }) ?? []),
        ];
      },
      success: () => {},
    },
    loadOfferDetails: {
      trigger: (
        state,
        _action: PayloadAction<{
          request: { offerRequestId: string; offerId: string };
        }>
      ) => {
        state.offerDetailedResponse = null;
        state.offerDetailLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{ response: OfferDetailedResponse }>
      ) => ({
        ...state,
        offerDetailedResponse: {
          ...action.response,
          offerSiteList: action.response.offerSiteList?.map((site) => ({
            ...site,
            availableBasketDiscounts: [
              ...(site.availableBasketDiscounts ?? []),
            ]?.sort(
              (a, b) => (a.minNetOrderValue ?? 0) - (b.minNetOrderValue ?? 0)
            ),
            items: site.items?.map((item) => ({
              ...item,
              availableQuantityDiscounts: [
                ...(item.availableQuantityDiscounts ?? []),
              ]?.sort((a, b) => (a.minQuantity ?? 0) - (b.minQuantity ?? 0)),
            })),
          })),
        },
        offerDetailLoading: false,
      }),
      failure: (state) => ({
        ...state,
        offerDetailLoading: false,
      }),
    },
    updateOfferSiteDeliveryPrice: {
      trigger: (
        state,
        action: PayloadAction<{
          request: {
            siteId: string;
            newDeliveryPrice: PriceWrapper | undefined;
          };
        }>
      ) => ({
        ...state,
        offerDetailedResponse: {
          ...state.offerDetailedResponse,
          offerSiteList: state.offerDetailedResponse?.offerSiteList?.map(
            (site) => {
              if (site.siteId === action.request.siteId) {
                return {
                  ...site,
                  sumDeliveryPrice: {
                    grossPrice: action.request.newDeliveryPrice?.grossPrice,
                    netPrice: action.request.newDeliveryPrice?.netPrice,
                  },
                };
              } else {
                return site;
              }
            }
          ),
        },
      }),
      success: () => {},
    },
    updateOfferApprovalRequest: {
      trigger: (
        state,
        action: PayloadAction<{
          siteId?: string;
          comment?: string;
          paymentMethod?: string;
          missingRetailerLicenseNumber?: string;
        }>
      ) => {
        if ((action.comment || action.comment === '') && action.siteId) {
          state.offerApprovalRequest.offerSiteIdCommentMap = {
            ...state.offerApprovalRequest.offerSiteIdCommentMap,
            [action.siteId]: action.comment,
          };
          state.offerApprovalRequest.offerSiteIdCommentMap = paramConvert(
            state.offerApprovalRequest.offerSiteIdCommentMap
          );
        }
        if (action.paymentMethod && action.siteId) {
          state.offerApprovalRequest.offerSiteIdPaymentMethod[action.siteId] =
            action.paymentMethod as PaymentMethodType;
        }
        if (action.missingRetailerLicenseNumber) {
          state.offerApprovalRequest.missingRetailerLicenseNumber =
            action.missingRetailerLicenseNumber;
        }
      },
      success: () => {},
    },
    deleteFromOfferApprovalRequest: {
      trigger: (
        state,
        action: PayloadAction<{
          siteId: string;
        }>
      ) => {
        let tmpCommentMap = {
          ...state.offerApprovalRequest.offerSiteIdCommentMap,
        };
        const { [action.siteId]: _, ...newCommentMap } = tmpCommentMap;
        let tmpPaymentMethodMap = {
          ...state.offerApprovalRequest.offerSiteIdPaymentMethod,
        };
        const { [action.siteId]: __, ...newPaymentMethodMap } =
          tmpPaymentMethodMap;
        state.offerApprovalRequest = {
          ...state.offerApprovalRequest,
          offerSiteIdCommentMap: newCommentMap,
          offerSiteIdPaymentMethod: newPaymentMethodMap as {
            [key: string]:
              | 'CASH'
              | 'CARD'
              | 'BANK_TRANSFER'
              | 'CASH_ON_DELIVERY';
          },
        };
      },
      success: () => {},
    },
    resetOfferApprovalRequest: {
      trigger: (state) => ({
        ...state,
        offerApprovalRequest: { offerSiteIdPaymentMethod: {} },
      }),
      success: () => {},
    },
    approveOffer: {
      trigger: (
        state,
        _action: PayloadAction<{
          request: {
            offerRequestId: string;
            offerId: string;
          };
        }>
      ) => {
        state.offerLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{ response: Array<OfferApprovalResponse> }>
      ) => {
        state.offerLoading = false;
      },
      failure: (state) => ({
        ...state,
        offerLoading: false,
      }),
    },
    recalculateOffer: {
      trigger: (
        state,
        action: PayloadAction<{ request: { offerRequestId: string } }>
      ) => {
        state.offerCalculationStatus = OfferCalculationStatus.IN_PROGRESS;
      },
      success: () => {},
    },
    updateOffer: {
      trigger: (
        _state,
        action: PayloadAction<{
          body: {
            offerId: string;
            offerRequestId: string;
            quantityChangeBulk: {
              [key1: string]: {
                [key2: string]: number;
              };
            };
          };
        }>
      ) => {},
      success: () => {},
    },
    updateQuantity: {
      trigger: (
        state,
        action: PayloadAction<{
          request: {
            siteId: string;
            packagingUnitId: string;
            quantity: number;
          };
        }>
      ) => {
        let fixSite = state.offerDetailedResponse?.offerSiteList?.find(
          (site) => site.siteId === action.request.siteId
        );
        let fixItem = fixSite?.items?.find(
          (item) => item.packagingUnitId === action.request.packagingUnitId
        );
        let newDiscountedPrice = fixItem?.availableQuantityDiscounts?.find(
          (discount) =>
            (discount.maxQuantity ?? 9999999) >=
              (action.request.quantity ?? 0) &&
            (discount.minQuantity ?? 0) <= (action.request.quantity ?? 0)
        )?.discountedUnitPrice;
        fixSite = {
          ...fixSite,
          items: fixSite?.items?.map((item) => {
            item.packagingUnitId === fixItem?.packagingUnitId
              ? (item = {
                  ...item,
                  quantity: action.request.quantity,
                  discountedPrice: newDiscountedPrice,
                  sumPrice: {
                    grossPrice:
                      action.request.quantity *
                      (newDiscountedPrice?.grossPrice ??
                        fixItem?.unitPrice?.grossPrice ??
                        0),

                    netPrice:
                      action.request.quantity *
                      (newDiscountedPrice?.netPrice ??
                        fixItem?.unitPrice?.netPrice ??
                        0),
                    vat: fixItem?.unitPrice?.vat,
                    vatRate: fixItem?.unitPrice?.vatRate,
                  },
                })
              : '';
            return item;
          }),
        };
        state.offerDetailedResponse = {
          ...state.offerDetailedResponse,
          offerSiteList: state.offerDetailedResponse?.offerSiteList?.map(
            (site) => {
              return site.siteId === action.request.siteId ? fixSite : site;
            }
          ) as WritableDraft<OfferSiteResponse>[] | undefined,
        };
        state.offerDetailedResponse = { ...state.offerDetailedResponse };
      },
      success: () => {},
    },
    deleteSiteFromDetails: {
      trigger: (
        state,
        action: PayloadAction<{ request: { siteId: string } }>
      ) => {
        state.offerDetailedResponse = {
          ...state.offerDetailedResponse,
          offerSiteList: state.offerDetailedResponse?.offerSiteList?.filter(
            (site) => site.siteId !== action.request.siteId
          ),
        };
      },
      success: () => {},
    },
    acceptMerchantsContract: {
      trigger: (
        state,
        action: PayloadAction<{ request: { merchantId: string } }>
      ) => {
        state.offerDetailedResponse = {
          ...state.offerDetailedResponse,
          offerSiteList: state.offerDetailedResponse?.offerSiteList?.map(
            (site) => ({
              ...site,
              contract:
                site.merchantId === action.request.merchantId
                  ? {
                      ...site.contract,
                      acceptedAt: new Date().toDateString(),
                    }
                  : { ...site.contract },
            })
          ),
        };
      },
      success: () => {},
    },
    exportOfferDetails: {
      trigger: (
        _state,
        _action: PayloadAction<{
          request: { offerRequestId: string; offerId: string };
        }>
      ) => {},
      success: () => {},
      failure: () => {},
    },
    reset: (_) => initialState,
  },
});

export const OffersSelectors = {
  ...selectors,
  selectIfOfferHasAnyDrsFee: createSelector(
    selectors.selectOfferDetailedResponse,
    (offer) =>
      offer?.offerSiteList?.some((site) => (site.sumItemsDrsPrice ?? 0) > 0)
  ),
  selectOfferSumPrice: createSelector(
    selectors.selectOfferDetailedResponse,
    (offer) => {
      const sumOfItemsSumPrice = offer?.offerSiteList
        ?.map((site) =>
          site.items?.reduce(
            (prev, curr) => ({
              net: (prev.net ?? 0) + (curr.sumPrice?.netPrice ?? 0),
              gross: (prev.gross ?? 0) + (curr.sumPrice?.grossPrice ?? 0),
            }),
            {
              net: 0,
              gross: 0,
            }
          )
        )
        .reduce(
          (prev, curr) => ({
            net: (prev?.net ?? 0) + (curr?.net ?? 0),
            gross: (prev?.gross ?? 0) + (curr?.gross ?? 0),
          }),
          {
            net: 0,
            gross: 0,
          }
        );
      const sumOfIDeliveryPrices = offer?.offerSiteList
        ?.map((site) => ({
          net: site.sumDeliveryPrice?.netPrice,
          gross: site.sumDeliveryPrice?.grossPrice,
        }))
        .reduce(
          (prev, curr) => ({
            net: (prev?.net ?? 0) + (curr?.net ?? 0),
            gross: (prev?.gross ?? 0) + (curr?.gross ?? 0),
          }),
          {
            net: 0,
            gross: 0,
          }
        );
      const sumOfBasketDiscounts = offer?.offerSiteList
        ?.map((site) => ({
          net: site.basketDiscountPrice?.netPrice,
          gross: site.basketDiscountPrice?.grossPrice,
        }))
        .reduce(
          (prev, curr) => ({
            net: (prev?.net ?? 0) + (curr?.net ?? 0),
            gross: (prev?.gross ?? 0) + (curr?.gross ?? 0),
          }),
          {
            net: 0,
            gross: 0,
          }
        );
      const sumOfDrsFee = offer?.offerSiteList
        ?.map((site) => site.sumItemsDrsPrice)
        .reduce((prev, curr) => (prev ?? 0) + (curr ?? 0));
      return {
        net:
          (sumOfItemsSumPrice?.net ?? 0) +
          (sumOfIDeliveryPrices?.net ?? 0) -
          (sumOfBasketDiscounts?.net ?? 0) +
          (sumOfDrsFee ?? 0),
        gross:
          (sumOfItemsSumPrice?.gross ?? 0) +
          (sumOfIDeliveryPrices?.gross ?? 0) -
          (sumOfBasketDiscounts?.gross ?? 0) +
          (sumOfDrsFee ?? 0),
      };
    }
  ),
  selectStoredSiteData: createSelector(
    selectors.selectOfferDetailedResponse,
    (offer) => {
      return offer?.offerSiteList?.map((site) => {
        const sumCountInCollection = site.items?.reduce(
          (prev, curr) =>
            (prev ?? 0) +
            (curr.additionalDepositFee
              ? (curr.countInCollection ?? 0) * (curr.quantity ?? 0)
              : 0),
          0
        );

        const additionalDrsFee = {
          countInCollection: sumCountInCollection,
          drsFee: (sumCountInCollection ?? 0) * 50,
        };

        const sumPrice = {
          netPrice:
            site.items?.reduce(
              (prev, curr) => (prev ?? 0) + (curr.sumPrice?.netPrice ?? 0),
              0
            ) ?? 0,
          grossPrice:
            site.items?.reduce(
              (prev, curr) => (prev ?? 0) + (curr.sumPrice?.grossPrice ?? 0),
              0
            ) ?? 0,
        };
        const sumWeight = site.items?.reduce(
          (prev, curr) =>
            (prev ?? 0) + (curr.unitWeight ?? 0) * (curr.quantity ?? 0),
          0
        );
        const orderValueRange = site.orderValueBasedPricingRanges?.find(
          (range) =>
            range.maximumOrderNetValue! >= sumPrice.netPrice! &&
            range.minimumOrderNetValue! <= sumPrice.netPrice!
        );
        const weightRange = site.weightBasedDeliveryPricingRanges?.find(
          (range) =>
            range.maximumOrderNetValue! >= sumWeight! &&
            range.minimumOrderNetValue! <= sumWeight!
        );
        const deliveryPrice = {
          cashOnDelivery:
            (orderValueRange?.cashOnDeliveryFee?.grossPrice ?? 0) +
            (orderValueRange?.fee?.grossPrice ?? 0) +
            (weightRange?.orderMinimumNetValueForFree
              ? weightRange?.orderMinimumNetValueForFree <=
                (sumPrice.netPrice ?? 0)
                ? 0
                : weightRange?.fee?.grossPrice ?? 0
              : weightRange?.fee?.grossPrice ?? 0) +
            (weightRange?.cashOnDeliveryFee?.grossPrice ?? 0),
          normal:
            (orderValueRange?.fee?.grossPrice ?? 0) +
            (weightRange?.orderMinimumNetValueForFree
              ? weightRange?.orderMinimumNetValueForFree <=
                (sumPrice.netPrice ?? 0)
                ? 0
                : weightRange?.fee?.grossPrice ?? 0
              : weightRange?.fee?.grossPrice ?? 0),
        };
        const basketIndex = site.availableBasketDiscounts?.findIndex(
          (discount, index) =>
            (discount.minNetOrderValue ?? 0) <= (sumPrice.netPrice ?? 0) &&
            (discount.maxNetOrderValue ?? 0) >= (sumPrice.netPrice ?? 0)
        );
        const basketDiscounts = {
          actual:
            basketIndex || basketIndex === 0
              ? site.availableBasketDiscounts?.[basketIndex]
              : undefined,
          next:
            basketIndex && basketIndex > -1
              ? site.availableBasketDiscounts?.[basketIndex + 1]
              : (site.availableBasketDiscounts?.length ?? 0) > 0 &&
                (site.availableBasketDiscounts?.[0].minNetOrderValue ?? 0) >
                  (sumPrice.netPrice ?? 0)
              ? site.availableBasketDiscounts?.[0]
              : undefined,
        };

        let nextQuantityDiscounts: { [key: string]: any } = {};
        site.items?.forEach((item) => {
          const index = item.availableQuantityDiscounts?.findIndex(
            (discount) =>
              (discount.minQuantity ?? 0) <= item.quantity! &&
              (discount.maxQuantity ?? 99999) >= item.quantity!
          );
          nextQuantityDiscounts[item.packagingUnitId!] =
            index || index === 0
              ? item.availableQuantityDiscounts?.[index + 1]
              : (item.availableQuantityDiscounts?.length ?? 0) > 0
              ? item.availableQuantityDiscounts?.[0]
              : undefined;
        });

        return {
          siteId: site.siteId,
          sumPrice: sumPrice,
          deliveryPrice: deliveryPrice,
          basketDiscounts: basketDiscounts,
          nextQuantityDiscounts: nextQuantityDiscounts,
          additionalDrsFee: additionalDrsFee,
        };
      });
    }
  ),
  selectOrderDisabled: createSelector(
    selectors.selectOfferDetailedResponse,
    selectors.selectOfferApprovalRequest,
    (offer, request) => {
      return (
        !offer?.offerSiteList?.every(
          (site) =>
            Object.keys(request.offerSiteIdPaymentMethod).includes(
              site.siteId ?? ''
            ) ||
            (site.contract && !site.contract?.acceptedAt)
        ) ||
        offer?.offerSiteList?.every(
          (site) => site.contract && !site.contract?.acceptedAt
        )
      );
    }
  ),
  selectCanContinueFromRecalculate: createSelector(
    selectors.selectOfferDetailedResponse,
    (offer) => {
      const sumOfSites = offer?.offerSiteList?.map((site) => {
        return {
          siteId: site.siteId,
          sumPrice: site.items?.reduce(
            (prev, curr) => (prev ?? 0) + (curr.sumPrice?.netPrice ?? 0),
            0
          ),
        };
      });

      return {
        isEveryOfferSumAboveLimit: offer?.offerSiteList?.every(
          (site) =>
            (sumOfSites?.find((inSite) => inSite.siteId === site.siteId)
              ?.sumPrice ?? 0) >= (site.minimumOrderValue ?? 0)
        ),
        isEveryOfferBelowLimit: offer?.offerSiteList?.every(
          (site) =>
            (sumOfSites?.find((inSite) => inSite.siteId === site.siteId)
              ?.sumPrice ?? 0) < (site.minimumOrderValue ?? 0)
        ),
      };
    }
  ),
  selectIsAllContractAccepted: createSelector(
    selectors.selectOfferDetailedResponse,
    (offer) =>
      offer?.offerSiteList?.every(
        (site) => !site.contract || (site.contract && site.contract.acceptedAt)
      )
  ),
};
